/**
 * DashboardShell — reusable dashboard layout wrapper (task:offers-in-shell,
 * task 1107). Renders the persistent sidebar + header frame so non-dashboard
 * routes (currently `/offers`, `/offers/:id`, `/market/agents/:id`) mount
 * inside the authed shell without duplicating shell markup.
 *
 * Extracted from DashboardPage: re-uses the exported DesktopSidebar and
 * DashboardHeader components with minimal props. Readiness data is sourced
 * from the shared module-level useReadiness store — if the user arrived via
 * /dashboard first the cache is warm; on a cold landing the fetch fires
 * (enabled only when signed in).
 *
 * Sidebar width: routes through `computeSidebarWidth` and the shared
 * `DashboardSidebarGrid`. Its provider lives above the router, so this shell
 * inherits the previous page's width during route remounts and animates to the
 * browse rail or full-sidebar destination without a snap.
 *
 * Preview-safety (M3 fix): Clerk's `useAuth()` throws when called outside a
 * `ClerkProvider`. DEV preview paths for marketplace/offers bypass the provider
 * (they are fixture-only and must work with no Clerk key — CI baseline
 * requirement). `DashboardShell` therefore dispatches to either
 * `AuthedDashboardShellInner` (real users, Clerk present) or
 * `PreviewDashboardShellInner` (DEV fixture paths, no Clerk), keeping the
 * authed path byte-identical to its pre-fix shape.
 */

import type { ReactNode } from "react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";

import { buildDashboardSnapshot } from "../../lib/dashboard";
import { getBrowserPathname, isDevPreviewPath } from "../../lib/dev-preview";
import { useReadiness } from "../../hooks/useReadiness";
import { useDashboardShellCapabilities } from "../../hooks/useDashboardShellCapabilities";
import { ReferralBannerContainer } from "../referral/referral-banner";
import { marketplaceShellDestination } from "../../lib/marketplace-route";
import { useOffersStore } from "../../stores/offers";
import { useNotificationsStore } from "../../stores/notifications";
import { computeSidebarWidth } from "../../pages/DashboardPage.helpers";
import {
  DashboardHeader,
  DesktopSidebar,
  MobileSidebar,
  MobileTabBar,
  type DashboardHeaderBreadcrumbItem,
} from "../../pages/DashboardPage";
import { DashboardSidebarGrid } from "./DashboardSidebarGrid";
import { showsMobileNavigation } from "./mobile-navigation";

// ── Shared shell frame (no auth dependency) ───────────────────────────────────

interface ShellFrameProps {
  /**
   * Which nav item is active. When omitted, falls back to
   * `marketplaceShellDestination(location.pathname)` (plan 291 simplify
   * pass 2 — that fallback highlights nothing, rather than defaulting to
   * "offers", for a route it doesn't recognize).
   */
  activeView?: Parameters<typeof DesktopSidebar>[0]["activeView"];
  /**
   * Optional strip rendered at the top of the content region (above
   * children). Supplied ONLY by the authed inner — the preview inner never
   * passes one, so `/preview/*` fixture paths render no banner.
   */
  banner?: ReactNode;
  breadcrumbItems?: readonly DashboardHeaderBreadcrumbItem[];
  children: ReactNode;
  readiness: Parameters<typeof DesktopSidebar>[0]["readiness"];
  readinessRefreshing: boolean;
  readinessTarget: Parameters<typeof DesktopSidebar>[0]["readinessTarget"];
  suppressClerkControls?: boolean;
}

/**
 * Pure layout frame — sidebar + header + content region. Called by both the
 * authed inner and the preview inner; never calls useAuth() itself.
 */
function DashboardShellFrame({
  activeView: activeViewProp,
  banner,
  breadcrumbItems,
  children,
  readiness,
  readinessRefreshing,
  readinessTarget,
  suppressClerkControls = false,
}: ShellFrameProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isMobileScoreOpen, setIsMobileScoreOpen] = useState(false);
  const offersCount = useOffersStore((s) => s.count);
  const feedUnreadCount = useNotificationsStore((s) => s.unreadCount);
  const { feedEnabled, marketplaceEnabled } = useDashboardShellCapabilities();
  // Explicit prop (e.g. activeView="feed" for the notification inbox) wins;
  // otherwise derive the highlighted destination from the route: the thread
  // surfaces highlight the sidebar "Messages" item (messaging-inbox IA WS6 —
  // without this branch, marketplaceShellDestination's "offers" fallback
  // would highlight Your Offers on /threads), then marketplace routes.
  const activeView =
    activeViewProp ??
    (location.pathname.startsWith("/threads") ||
    location.pathname.startsWith("/preview/threads")
      ? "messages"
      : marketplaceShellDestination(location.pathname));
  const usesBrowseWorkspace = location.pathname.endsWith("/marketplace/browse");
  const [isBrowseRailExpanded, setIsBrowseRailExpanded] = useState(false);

  const snapshot = buildDashboardSnapshot();
  const showMobileNavigation = showsMobileNavigation(location.pathname);
  return (
    <div
      data-slot="dashboard-shell"
      data-mobile-navigation={showMobileNavigation}
      className={`min-h-dvh bg-bg-warm lg:h-dvh lg:min-h-0 lg:overflow-hidden lg:pb-0 ${showMobileNavigation ? "pb-[calc(6rem+env(safe-area-inset-bottom))]" : "pb-0"}`}
    >
      <DashboardSidebarGrid
        sidebarWidth={computeSidebarWidth(
          activeView,
          isBrowseRailExpanded,
          usesBrowseWorkspace,
        )}
      >
        <DesktopSidebar
          activeView={activeView}
          feedEnabled={feedEnabled}
          isRailExpanded={isBrowseRailExpanded}
          marketplaceEnabled={marketplaceEnabled}
          offersCount={offersCount}
          onNavigate={(view) => navigate(`/dashboard?view=${view}`)}
          onNavigatePath={(path) => navigate(path)}
          onOpenSettings={() => navigate("/dashboard/settings")}
          onToggleRailExpanded={() =>
            setIsBrowseRailExpanded((expanded) => !expanded)
          }
          readiness={readiness}
          readinessRefreshing={readinessRefreshing}
          readinessTarget={readinessTarget}
          snapshot={snapshot}
          suppressClerkControls={suppressClerkControls}
          variant={usesBrowseWorkspace ? "rail" : "default"}
        />

        <div
          className={
            usesBrowseWorkspace
              ? "min-w-0 lg:flex lg:h-full lg:min-h-0 lg:flex-col lg:overflow-hidden"
              : "min-w-0 px-4 py-5 sm:px-5 lg:h-full lg:min-h-0 lg:overflow-y-auto lg:overscroll-none lg:px-6 lg:py-6"
          }
          data-slot="dashboard-content-frame"
        >
          <div
            className={
              usesBrowseWorkspace ? "px-4 pt-5 sm:px-5 lg:px-6 lg:pt-6" : ""
            }
          >
            <DashboardHeader
              activeView={activeView}
              breadcrumbItems={breadcrumbItems}
              feedUnreadCount={feedUnreadCount}
              isMobileNavOpen={isMobileNavOpen}
              isMobileScoreOpen={isMobileScoreOpen}
              onNavigateOverview={() => navigate("/dashboard")}
              onOpenAdvisor={() => navigate("/dashboard?view=advisor")}
              onToggleMobileNav={() => setIsMobileNavOpen((v) => !v)}
              onToggleMobileScore={() => setIsMobileScoreOpen((v) => !v)}
              readiness={readiness}
              readinessRefreshing={readinessRefreshing}
            />
          </div>
          <main
            className={
              usesBrowseWorkspace
                ? "min-w-0 pt-4 lg:min-h-0 lg:flex-1 lg:pt-0"
                : "min-w-0 pt-4"
            }
          >
            {banner}
            {children}
          </main>
        </div>
      </DashboardSidebarGrid>

      {showMobileNavigation && (
        <MobileTabBar
          activeView={activeView}
          marketplaceEnabled={marketplaceEnabled}
          onNavigate={(view) => navigate(`/dashboard?view=${view}`)}
          onNavigatePath={(path) => navigate(path)}
          onOpenSettings={() => navigate("/dashboard/settings")}
        />
      )}
      <MobileSidebar
        activeView={activeView}
        feedEnabled={feedEnabled}
        isOpen={isMobileNavOpen}
        marketplaceEnabled={marketplaceEnabled}
        onClose={() => setIsMobileNavOpen(false)}
        onNavigate={(view) => {
          setIsMobileNavOpen(false);
          setIsMobileScoreOpen(false);
          navigate(`/dashboard?view=${view}`);
        }}
        onNavigatePath={(path) => {
          setIsMobileNavOpen(false);
          setIsMobileScoreOpen(false);
          navigate(path);
        }}
        onOpenSettings={() => {
          setIsMobileNavOpen(false);
          setIsMobileScoreOpen(false);
          navigate("/dashboard/settings");
        }}
      />
    </div>
  );
}

// ── Authed inner — calls useAuth(), identical to the pre-fix DashboardShell ──

interface InnerProps {
  activeView?: ShellFrameProps["activeView"];
  breadcrumbItems?: readonly DashboardHeaderBreadcrumbItem[];
  children: ReactNode;
}

function AuthedDashboardShellInner({
  activeView,
  breadcrumbItems,
  children,
}: InnerProps) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const location = useLocation();

  // Reuses the shared readiness store — warm if the user came via /dashboard,
  // fetches once otherwise. `enabled: false` prevents fetches on unauthed paths.
  const activeReadiness = useReadiness({
    enabled: isLoaded && !!isSignedIn,
    getToken,
  });

  // Referral discovery banner on the Offers surfaces (Overview/Passport mount
  // theirs from DashboardPage with the bootstrap-decoded dismissal). Scoped to
  // /offers — the threads/feed workspaces are lists, not landing surfaces.
  const showReferralBanner =
    isLoaded && !!isSignedIn && location.pathname.startsWith("/offers");

  return (
    <DashboardShellFrame
      activeView={activeView}
      banner={showReferralBanner ? <ReferralBannerContainer /> : undefined}
      breadcrumbItems={breadcrumbItems}
      readiness={activeReadiness.data?.result ?? null}
      readinessRefreshing={activeReadiness.isLoading}
      readinessTarget={activeReadiness.data?.target ?? null}
    >
      {children}
    </DashboardShellFrame>
  );
}

// ── Preview inner — NO useAuth(), safe with no ClerkProvider ─────────────────

/**
 * Used only in DEV for fixture-only preview paths (`/preview/marketplace/*`,
 * `/preview/offers`, etc.) where ClerkProvider is intentionally not mounted.
 *
 * Uses the real frame, sidebar, and header. Only the Clerk-coupled Plus control
 * is omitted so visual evidence still proves route-aware shell integration.
 */
function PreviewDashboardShellInner({
  activeView,
  breadcrumbItems,
  children,
}: InnerProps) {
  return (
    <DashboardShellFrame
      activeView={activeView}
      breadcrumbItems={breadcrumbItems}
      readiness={null}
      readinessRefreshing={false}
      readinessTarget={null}
      suppressClerkControls
    >
      {children}
    </DashboardShellFrame>
  );
}

// ── Public export: preview-safe dispatch ──────────────────────────────────────

export function DashboardShell({
  activeView,
  breadcrumbItems,
  children,
}: {
  /**
   * Which sidebar item is highlighted. When omitted, derived from the
   * current path (plan 291 simplify pass 2) — an unrecognized path
   * highlights nothing, never a default "offers".
   */
  activeView?: ShellFrameProps["activeView"];
  breadcrumbItems?: readonly DashboardHeaderBreadcrumbItem[];
  children: ReactNode;
}) {
  // In DEV preview bypass context, ClerkProvider is not mounted for
  // fixture-only paths. Render the preview-safe inner to avoid useAuth()
  // throwing outside a ClerkProvider → ErrorBoundary.
  //
  // import.meta.env.DEV is statically false in production builds so Vite
  // tree-shakes the preview branch entirely — the authed path is unchanged.
  if (import.meta.env.DEV && isDevPreviewPath(getBrowserPathname())) {
    return (
      <PreviewDashboardShellInner
        activeView={activeView}
        breadcrumbItems={breadcrumbItems}
      >
        {children}
      </PreviewDashboardShellInner>
    );
  }

  return (
    <AuthedDashboardShellInner
      activeView={activeView}
      breadcrumbItems={breadcrumbItems}
    >
      {children}
    </AuthedDashboardShellInner>
  );
}
